/**
 * Shared glance surfaces for a running mission: run controls, progress,
 * and the live assigned roster. Used on the left briefing panel so the
 * dock Live tab can keep Sentinel and alerts.
 */
import React, { useMemo, useState } from 'react';
import {
  Box,
  Dialog,
  DialogContent,
  LinearProgress,
  Slider,
  Typography,
} from '@mui/material';
import { RichTooltip } from '../../components/common';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Mission,
  MissionAlternateRoute,
  MissionNamedRoute,
  MissionDeviceState,
  MissionProgress,
  MissionRouteEventType,
  MissionRun,
  updateMission,
} from '../../services/missionApi';
import { DeviceGroup, deviceKindLabel, groupDevices } from './deviceGroups';
import { formatClock, formatMeters, formatSpan, formatSpeed, formatWhen } from './format';
import { metalSx, type MetalFinish } from '../../theme/metalButtons';

export const GOLD = '#f0b429';
export const RED = '#ef5350';
export const GREEN = '#57c96b';
export const BLUE = '#42a5f5';
export const MUTED = '#8a939f';
export const PAPER = '#e8ebee';
export const INK = '#16181c';
export const TEAL = '#00b8a9';
const ALTERNATE_TEAL = '#00b8a9';
const EDGE_HI = '#333a45';

export const EVENT_COLOR: Record<MissionRouteEventType, string> = {
  off_route: RED,
  back_on_route: GREEN,
  signal_lost: GOLD,
  signal_restored: GREEN,
  waypoint_reached: BLUE,
  cotraveler_suspected: GOLD,
  cotraveler_confirmed: RED,
  cotraveler_sustained: RED,
  watchlist_on_route: RED,
  surveillance_ahead: RED,
  uas_intercept: RED,
  threat_reacquired: RED,
  corridor_threat: GOLD,
  wifi_located: MUTED,
  ble_located: MUTED,
  cell_located: MUTED,
  route_changed: BLUE,
  threat_stale: MUTED,
  field_report: BLUE,
  rf_multiloc: GOLD,
  operator_alert: BLUE,
};

/** Fixed collection sites: neither on nor off the route, so neither colour. */
const CYAN = '#4dd0e1';

function stateColor(device: MissionDeviceState): string {
  if (device.signal_state === 'lost') return GOLD;
  if (device.is_stationary) return device.signal_state === 'live' ? CYAN : MUTED;
  if (device.route_state === 'off_route') return RED;
  if (device.route_state === 'on_route') return GREEN;
  return MUTED;
}

function stateText(device: MissionDeviceState, units: 'km' | 'mi'): string {
  if (device.signal_state === 'lost') {
    return device.last_fix_at ? `no fix since ${formatWhen(device.last_fix_at)}` : 'no position yet';
  }
  // A fixed site has no relationship to the line worth reporting.
  if (device.is_stationary) {
    return device.signal_state === 'live' ? 'fixed site · collecting' : 'fixed site';
  }
  const distance = formatMeters(device.distance_m, units);
  if (device.route_state === 'off_route') return `off route by ${distance}`;
  if (device.route_state === 'on_route') return distance ? `on route · ${distance} off centre` : 'on route';
  return 'waiting for a fix';
}

export const Action: React.FC<{
  onClick: () => void;
  disabled?: boolean;
  accent?: boolean;
  danger?: boolean;
  filled?: boolean;
  hazard?: boolean;
  children: React.ReactNode;
}> = ({ onClick, disabled, accent, danger, filled, hazard, children }) => {
  const finish: MetalFinish = hazard
    ? 'hazard'
    : danger
      ? 'anodized'
      : filled
        ? 'steel'
        : accent
          ? 'titanium'
          : 'gun';
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={disabled ? undefined : onClick}
      sx={{
        ...metalSx(finish),
        height: 26,
        px: 1.1,
        display: 'inline-flex',
        alignItems: 'center',
        fontFamily: 'inherit',
        fontSize: '0.68rem',
        fontWeight: 700,
        letterSpacing: 0.2,
        whiteSpace: 'nowrap',
        cursor: disabled ? 'default' : 'pointer',
      }}
    >
      {children}
    </Box>
  );
};

const RosterLine: React.FC<{
  device: MissionDeviceState;
  name: string;
  units: 'km' | 'mi';
  inset?: boolean;
  bold?: boolean;
  children?: React.ReactNode;
}> = ({ device, name, units, inset, bold, children }) => (
  <>
    <Box sx={{ width: 7, height: 7, borderRadius: '50%', bgcolor: stateColor(device), flexShrink: 0, ml: inset ? 1.75 : 0 }} />
    <Typography variant="caption" noWrap sx={{ minWidth: 0, fontWeight: bold ? 600 : 400 }}>
      {name}
    </Typography>
    {children}
    <Box sx={{ flex: 1, minWidth: 4 }} />
    {device.distance_along_m != null && device.signal_state !== 'lost' && (
      <Typography variant="caption" sx={{ fontSize: '0.62rem', color: 'text.disabled', flexShrink: 0 }}>
        {formatMeters(device.distance_along_m, units)} in
      </Typography>
    )}
    <Typography
      variant="caption"
      sx={{
        fontSize: '0.65rem',
        flexShrink: 0,
        color: device.route_state === 'off_route' ? RED : 'text.secondary',
      }}
    >
      {stateText(device, units)}
    </Typography>
  </>
);

const RosterGroup: React.FC<{
  group: DeviceGroup;
  deviceLabel: (deviceId: string) => string;
  units: 'km' | 'mi';
}> = ({ group, deviceLabel, units }) => {
  const [open, setOpen] = useState(false);
  const expandable = group.devices.length > 1;
  const name = group.label || deviceLabel(group.devices[0].device_id);

  return (
    <>
      <Box
        role={expandable ? 'button' : undefined}
        tabIndex={expandable ? 0 : undefined}
        aria-expanded={expandable ? open : undefined}
        onClick={expandable ? () => setOpen((v) => !v) : undefined}
        onKeyDown={(e) => {
          if (!expandable) return;
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setOpen((v) => !v); }
        }}
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 0.75,
          py: 0.15,
          borderRadius: 0.5,
          cursor: expandable ? 'pointer' : 'default',
          '&:hover': expandable ? { bgcolor: 'rgba(255,255,255,0.04)' } : undefined,
        }}
      >
        <RosterLine device={group.lead} name={name} units={units} bold={expandable}>
          {expandable && (
            <>
              <ExpandMoreIcon
                sx={{
                  fontSize: 13,
                  flexShrink: 0,
                  color: 'text.disabled',
                  transform: open ? 'none' : 'rotate(-90deg)',
                  transition: 'transform 120ms ease',
                }}
              />
              {group.quiet > 0 && (
                <Typography
                  variant="caption"
                  title={group.quietSince ? `Quiet since ${formatWhen(group.quietSince)}` : undefined}
                  sx={{ fontSize: '0.6rem', color: MUTED, flexShrink: 0 }}
                >
                  {group.quiet} quiet
                  {group.quietFor ? ` ${group.quietFor}` : ''}
                </Typography>
              )}
            </>
          )}
        </RosterLine>
      </Box>
      {expandable && open && group.devices.map((device) => (
        <Box key={device.device_id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, py: 0.15 }}>
          <RosterLine device={device} name={deviceKindLabel(device)} units={units} inset />
        </Box>
      ))}
    </>
  );
};

const Stat: React.FC<{
  label: string;
  value: string;
  tone?: string;
  hint?: string;
}> = ({ label, value, tone, hint }) => {
  const cell = (
    <Box sx={{ minWidth: 64, flex: '1 1 0', px: 0.25 }}>
      <Typography
        variant="body2"
        noWrap
        sx={{
          fontWeight: 800,
          lineHeight: 1.2,
          color: tone || '#e8ebee',
          fontVariantNumeric: 'tabular-nums',
          fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
          fontSize: '0.78rem',
        }}
      >
        {value}
      </Typography>
      <Typography
        variant="caption"
        noWrap
        sx={{ display: 'block', fontSize: '0.6rem', letterSpacing: 0.4, color: MUTED }}
      >
        {label.toUpperCase()}
      </Typography>
    </Box>
  );
  if (!hint) return cell;
  return <RichTooltip body={hint}>{cell}</RichTooltip>;
};

const SPEED_HINT: Record<string, string> = {
  planned: 'Not enough recent fixes to measure; using the pace the route was planned at.',
  rehearsed: 'Not enough recent fixes to measure; using the pace held on a previous run of this route.',
  stopped: 'The lead device is not advancing. The arrival below assumes it resumes at the planned pace.',
  observed: 'Measured from progress along the route over the last few minutes.',
};

export const RunBar: React.FC<{
  run: MissionRun | null;
  runs: MissionRun[];
  ready: boolean;
  busy: boolean;
  units: 'km' | 'mi';
  alternate?: MissionAlternateRoute | null;
  namedRoutes?: MissionNamedRoute[];
  activeRouteId?: string | null;
  onSwap: () => void;
  onActivateNamed?: (id: string) => void;
  onToggleSentinel?: () => void;
  sentinelOff?: boolean;
  /** One-click map destination: lead → tapped point. */
  onEmergencyReroute?: () => void;
  onCancelEmergencyReroute?: () => void;
  emergencyReroutePicking?: boolean;
  emergencyRerouteBusy?: boolean;
  emergencyRerouteError?: string | null;
  emergencyRerouteEnabled?: boolean;
}> = ({
  run, runs, ready, busy, units, alternate, namedRoutes = [], activeRouteId,
  onSwap, onActivateNamed, onToggleSentinel, sentinelOff,
  onEmergencyReroute, onCancelEmergencyReroute,
  emergencyReroutePicking, emergencyRerouteBusy, emergencyRerouteError,
  emergencyRerouteEnabled,
}) => {
  const finished = runs.filter((r) => r.state === 'complete');
  const rehearsed = finished.find((r) => r.kind === 'rehearsal' && r.observed_speed_mps);

  return (
    <Box sx={{ mb: 0.75 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {run ? (
          <>
            <Box
              role={run.kind === 'live' ? 'button' : undefined}
              title={run.kind === 'live' ? 'Sentinel live updates' : undefined}
              onClick={run.kind === 'live' ? onToggleSentinel : undefined}
              sx={{
                px: 0.5,
                borderRadius: 0.5,
                fontSize: '0.6rem',
                fontWeight: 800,
                letterSpacing: 0.4,
                color: '#111',
                bgcolor: run.kind === 'rehearsal' ? GOLD : TEAL,
                cursor: run.kind === 'live' ? 'pointer' : 'default',
                opacity: run.kind === 'live' && sentinelOff ? 0.45 : 1,
              }}
            >
              {run.kind === 'rehearsal' ? 'BASELINE' : 'LIVE'} {run.run_number}
            </Box>
            <Typography variant="caption" color="text.secondary" noWrap sx={{ flex: 1, minWidth: 0 }}>
              {run.kind === 'rehearsal' ? 'collecting, paging nobody' : 'convoy enrolled'}
              {run.started_at ? ` · from ${formatClock(run.started_at)}` : ''}
            </Typography>
          </>
        ) : (
          <Typography variant="caption" sx={{ fontWeight: 700, flex: 1, minWidth: 0 }}>
            {finished.length === 0 ? 'No runs yet' : `${finished.length} run${finished.length === 1 ? '' : 's'}`}
          </Typography>
        )}
      </Box>

      {!run && finished.length === 0 && ready && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', fontSize: '0.63rem' }}>
          A baseline run drives the route without paging anyone. It measures the pace for the
          arrival estimate and records which radios belong to this road, so the live run can be
          compared against it and tell a follower from the traffic.
        </Typography>
      )}

      {!run && rehearsed && (
        <Typography variant="caption" sx={{ display: 'block', fontSize: '0.63rem', color: GREEN }}>
          Arrival will use {formatSpeed(rehearsed.observed_speed_mps, units)} from baseline run{' '}
          {rehearsed.run_number}
          {rehearsed.route_signature ? '' : ' (route has changed since)'}.
        </Typography>
      )}

      {namedRoutes.filter((route) => route.geometry || route.id === (activeRouteId || 'primary')).map((route) => {
        const active = route.id === (activeRouteId || 'primary');
        return (
          <Box key={route.id} sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4 }}>
            <Box sx={{ width: 12, height: 2, bgcolor: active ? BLUE : GOLD, flexShrink: 0 }} />
            <Typography
              variant="caption"
              color="text.secondary"
              noWrap
              sx={{ flex: 1, minWidth: 0, fontSize: '0.63rem', fontWeight: active ? 700 : 400 }}
            >
              {route.label} {active ? 'active' : 'ready'}
              {route.stats?.duration ? ` · ${formatSpan(route.stats.duration)}` : ''}
            </Typography>
            {!active && (
              <Action
                onClick={() => onActivateNamed?.(route.id)}
                disabled={busy || !onActivateNamed}
              >
                Switch
              </Action>
            )}
          </Box>
        );
      })}
      {alternate && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 0.4 }}>
          <Box sx={{ width: 12, height: 2, bgcolor: ALTERNATE_TEAL, flexShrink: 0 }} />
          <Typography
            variant="caption"
            color="text.secondary"
            noWrap
            sx={{ flex: 1, minWidth: 0, fontSize: '0.63rem' }}
          >
            {alternate.label || 'Fallback'} ready
            {alternate.stats?.duration ? ` · ${formatSpan(alternate.stats.duration)}` : ''}
          </Typography>
          <Action onClick={onSwap} disabled={busy || !ready}>Switch</Action>
        </Box>
      )}
      {onEmergencyReroute && (
        <Box sx={{ mt: 0.65, '& > button': { width: '100%', justifyContent: 'center' } }}>
          <Action
            hazard
            onClick={() => (
              emergencyReroutePicking
                ? onCancelEmergencyReroute?.()
                : onEmergencyReroute()
            )}
            disabled={!emergencyRerouteEnabled || emergencyRerouteBusy || busy}
          >
            {emergencyRerouteBusy
              ? 'Routing…'
              : emergencyReroutePicking
                ? 'Tap map'
                : 'Emergency Reroute'}
          </Action>
          {emergencyReroutePicking && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: GOLD, fontSize: '0.62rem' }}>
              Click the map for the new destination — Esc to cancel
            </Typography>
          )}
          {emergencyRerouteError && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.35, color: RED, fontSize: '0.62rem' }}>
              {emergencyRerouteError}
            </Typography>
          )}
        </Box>
      )}
    </Box>
  );
};

function delayText(delaySeconds?: number | null): { text: string; tone: string } | null {
  if (delaySeconds == null || Math.abs(delaySeconds) < 120) return null;
  const span = formatSpan(Math.abs(delaySeconds));
  return delaySeconds > 0
    ? { text: `${span} behind plan`, tone: GOLD }
    : { text: `${span} ahead of plan`, tone: GREEN };
}

function arrived(progress: MissionProgress): boolean {
  return progress.stopped && (progress.remaining_m <= 40 || progress.progress_ratio >= 0.995);
}

export const ProgressStrip: React.FC<{
  progress: MissionProgress;
  units: 'km' | 'mi';
  elapsedS: number | null;
}> = ({ progress, units, elapsedS }) => {
  const there = arrived(progress);
  const delay = there ? null : delayText(progress.delay_s);
  const pct = Math.round(progress.progress_ratio * 100);
  return (
    <Box sx={{ mb: 0.75 }}>
      <Box sx={{ display: 'flex', gap: 0.5, mb: 0.5, alignItems: 'flex-end' }}>
        <Stat
          label="Progress"
          value={there ? 'ARRIVED' : `${pct}%`}
          tone={there ? GREEN : undefined}
          hint={`${formatMeters(progress.distance_along_m, units)} of ${formatMeters(progress.total_distance_m, units)}`}
        />
        {!there && <Stat label="Remaining" value={formatMeters(progress.remaining_m, units) || '—'} />}
        {progress.stopped && !there ? (
          <Box sx={{ minWidth: 64, flex: '1 1 0', px: 0.25 }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                height: 18,
                px: 0.7,
                borderRadius: 1.1,
                border: `1px solid ${EDGE_HI}`,
                bgcolor: 'rgba(240,180,41,0.10)',
              }}
            >
              <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: GOLD }} />
              <Typography sx={{ fontSize: '0.58rem', fontWeight: 700, color: '#c6cdd5', letterSpacing: 0.3 }}>
                STOPPED
              </Typography>
            </Box>
            <Typography variant="caption" sx={{ display: 'block', fontSize: '0.6rem', letterSpacing: 0.4, color: MUTED }}>
              SPEED
            </Typography>
          </Box>
        ) : (
          <Stat
            label="Speed"
            value={there ? '—' : formatSpeed(progress.speed_mps, units)}
            hint={SPEED_HINT[progress.speed_source || 'observed'] || SPEED_HINT.observed}
          />
        )}
        <Stat label="ETA" value={there ? '—' : formatClock(progress.eta_at)} />
        <Stat label="Elapsed" value={formatSpan(elapsedS)} />
      </Box>
      <LinearProgress
        variant="determinate"
        value={Math.min(100, Math.max(0, pct))}
        sx={{
          height: 4,
          borderRadius: 2,
          bgcolor: 'rgba(255,255,255,0.08)',
          '& .MuiLinearProgress-bar': {
            bgcolor: there ? GREEN : progress.stopped ? GOLD : TEAL,
            borderRadius: 2,
          },
        }}
      />
      {delay && (
        <Typography variant="caption" sx={{ display: 'block', mt: 0.35, fontSize: '0.64rem', color: delay.tone }}>
          {delay.text}
          {progress.planned_eta_at && ` · planned ${formatClock(progress.planned_eta_at)}`}
        </Typography>
      )}
    </Box>
  );
};

export const AssignedRoster: React.FC<{
  devices: MissionDeviceState[];
  deviceLabel: (deviceId: string) => string;
  units: 'km' | 'mi';
  empty?: string;
}> = ({ devices, deviceLabel, units, empty }) => {
  const groups = useMemo(() => groupDevices(devices), [devices]);
  if (devices.length === 0) {
    return empty ? (
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {empty}
      </Typography>
    ) : null;
  }
  return (
    <Box sx={{ mt: 0.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.75, mb: 0.15 }}>
        <Typography variant="caption" sx={{ fontWeight: 700 }}>Assigned</Typography>
        {groups.length < devices.length && (
          <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'text.disabled' }}>
            {devices.length} devices
          </Typography>
        )}
      </Box>
      {groups.map((group) => (
        <RosterGroup key={group.key} group={group} deviceLabel={deviceLabel} units={units} />
      ))}
    </Box>
  );
};

export const WatchSettingsDialog: React.FC<{
  open: boolean;
  mission: Mission;
  threshold: number;
  alertsOn: boolean;
  units: 'km' | 'mi';
  onThreshold: (metres: number) => void;
  onClose: () => void;
  onError: (message: string | null) => void;
  onMissionChange: () => void;
}> = ({ open, mission, threshold, alertsOn, units, onThreshold, onClose, onError, onMissionChange }) => (
  <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
    <DialogContent sx={{ pt: 2 }}>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.25 }}>
        Route watch
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
        While the mission is in progress, each assigned device is compared against the planned
        route every minute. An alert needs two consecutive fixes past the threshold, and clears
        once the device is well back inside, so one stray fix will not wake anyone up.
      </Typography>
      <Typography variant="caption" sx={{ fontWeight: 700, display: 'block' }}>
        Deviation threshold · {formatMeters(threshold, units)}
      </Typography>
      <Slider
        size="small"
        min={50}
        max={2000}
        step={50}
        value={threshold}
        onChange={(_, v) => onThreshold(v as number)}
        onChangeCommitted={async (_, v) => {
          try {
            await updateMission(mission.id, { deviation_threshold_m: v as number });
            onMissionChange();
          } catch (e: any) {
            onError(e?.response?.data?.detail || e?.message || 'Could not save the threshold');
          }
        }}
        sx={{ color: GOLD, mb: 1 }}
      />
      <Box sx={{ display: 'flex', gap: 2, mt: 1.5, justifyContent: 'space-between' }}>
        <Action
          onClick={async () => {
            try {
              await updateMission(mission.id, { alerts_enabled: !alertsOn });
              onMissionChange();
            } catch (e: any) {
              onError(e?.response?.data?.detail || e?.message || 'Could not change alerting');
            }
          }}
        >
          {alertsOn ? 'Turn route watch off' : 'Turn route watch on'}
        </Action>
        <Action accent onClick={onClose}>Done</Action>
      </Box>
    </DialogContent>
  </Dialog>
);
